import React, { useState, useEffect, useRef } from 'react';
import { saveClientData } from '../utils/api';
import { auth } from '../config/firebase';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { notifyOTPSent } from '../utils/notifications';

const ContactPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [resendTimer, setResendTimer] = useState(0); // Timer for resend OTP (60 seconds)
  const recaptchaVerifierRef = useRef(null);
  const recaptchaContainerRef = useRef(null);

  useEffect(() => {
    // Show popup after random time between 5-10 seconds on every page load/refresh
    const delay = Math.random() * 5000 + 5000; // 5000ms to 10000ms
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, delay);

    return () => clearTimeout(timer);
  }, []);

  // Initialize reCAPTCHA when popup is shown
  useEffect(() => {
    if (!showPopup || !auth) return;

    let initTimer;
    let interactionHandler;

    const initRecaptcha = () => {
      if (recaptchaVerifierRef.current) {
        console.log('✅ reCAPTCHA already initialized for popup');
        return; // Already initialized
      }

      // Verify auth instance is valid
      if (!auth) {
        console.error('❌ Firebase auth instance is not available');
        setError('Firebase authentication not initialized. Please refresh the page.');
        return;
      }

      if (!recaptchaContainerRef.current) {
        // Create a hidden container for reCAPTCHA
        const container = document.createElement('div');
        container.id = 'recaptcha-container-popup';
        container.style.display = 'none';
        document.body.appendChild(container);
        recaptchaContainerRef.current = container;
        console.log('✅ Created reCAPTCHA container for popup');
      }

      try {
        recaptchaVerifierRef.current = new RecaptchaVerifier(
          auth,
          recaptchaContainerRef.current,
          {
            size: 'invisible',
            callback: () => {
              console.log('✅ reCAPTCHA solved for popup');
            },
            'expired-callback': () => {
              console.error('❌ reCAPTCHA expired for popup');
              setError('reCAPTCHA expired. Please refresh the page.');
              recaptchaVerifierRef.current = null;
            },
            // Add error callback to handle fallback to visible reCAPTCHA
            'error-callback': () => {
              console.warn('⚠️ reCAPTCHA error - may fallback to visible challenge');
            }
          }
        );
        
        // Store globally for debugging
        window.recaptchaVerifierPopup = recaptchaVerifierRef.current;
        console.log('✅ reCAPTCHA initialized successfully for popup');
      } catch (error) {
        console.error('❌ Error initializing reCAPTCHA for popup:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
        
        // More specific error messages
        if (error.message && error.message.includes('domain')) {
          setError('Domain not authorized. Please check Firebase Console settings.');
        } else if (error.message && error.message.includes('network')) {
          setError('Network error loading reCAPTCHA. Please check your connection.');
        } else {
          // Don't show error if it's just falling back to visible reCAPTCHA
          if (!error.message || !error.message.includes('Enterprise')) {
            setError(`Failed to initialize reCAPTCHA: ${error.message || 'Unknown error'}. Please refresh the page.`);
          }
        }
        
        recaptchaVerifierRef.current = null;
      }
    };

    // Initialize on user interaction (faster) or after delay (fallback)
    interactionHandler = () => {
      initRecaptcha();
      document.removeEventListener('click', interactionHandler);
      document.removeEventListener('touchstart', interactionHandler);
    };
    
    document.addEventListener('click', interactionHandler, { once: true });
    document.addEventListener('touchstart', interactionHandler, { once: true });

    // Fallback: initialize after delay to ensure it's ready
    initTimer = setTimeout(() => {
      initRecaptcha();
      document.removeEventListener('click', interactionHandler);
      document.removeEventListener('touchstart', interactionHandler);
    }, 500);

    return () => {
      if (initTimer) clearTimeout(initTimer);
      document.removeEventListener('click', interactionHandler);
      document.removeEventListener('touchstart', interactionHandler);
      
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          // Ignore clear errors
        }
        recaptchaVerifierRef.current = null;
      }

      // Clean up reCAPTCHA container if it exists
      if (recaptchaContainerRef.current && recaptchaContainerRef.current.parentNode) {
        recaptchaContainerRef.current.parentNode.removeChild(recaptchaContainerRef.current);
        recaptchaContainerRef.current = null;
      }
    };
  }, [showPopup, auth]);

  // Countdown timer for resend OTP
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prevTimer) => {
          if (prevTimer <= 1) {
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [resendTimer]);

  const handleSkip = () => {
    setShowPopup(false);
  };

  const handleSendOtp = async () => {
    setError('');
    
    // Validate email
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate phone (Indian format)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phone || !phoneRegex.test(phone)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setSendingOtp(true);
    setError('');

    try {
      // Ensure reCAPTCHA is initialized
      if (!recaptchaVerifierRef.current) {
        console.log('⚠️ reCAPTCHA not initialized, attempting to initialize now...');
        
        // Try to initialize reCAPTCHA now
        if (!recaptchaContainerRef.current) {
          const container = document.createElement('div');
          container.id = 'recaptcha-container-popup';
          container.style.display = 'none';
          document.body.appendChild(container);
          recaptchaContainerRef.current = container;
        }

        try {
          recaptchaVerifierRef.current = new RecaptchaVerifier(
            auth,
            recaptchaContainerRef.current,
            {
              size: 'invisible',
              callback: () => {
                console.log('✅ reCAPTCHA solved for popup');
              },
              'expired-callback': () => {
                console.error('❌ reCAPTCHA expired for popup');
                recaptchaVerifierRef.current = null;
              },
              'error-callback': () => {
                console.warn('⚠️ reCAPTCHA error - may fallback to visible challenge');
              }
            }
          );
          console.log('✅ reCAPTCHA initialized on-demand');
        } catch (initError) {
          console.error('❌ Failed to initialize reCAPTCHA:', initError);
          // Don't block if it's just Enterprise config fallback
          if (initError.message && initError.message.includes('Enterprise')) {
            console.log('ℹ️ Falling back to visible reCAPTCHA v2');
            // Continue - Firebase will show visible challenge
          } else {
            setError('reCAPTCHA not ready. Please wait a moment and try again.');
            setSendingOtp(false);
            return;
          }
        }
      }

      // Save lead to database (before OTP verification)
      try {
        const leadData = {
          email: email.toLowerCase().trim(),
          phone: `+91${phone}`,
          source: 'popup',
          status: 'new',
        };
        
        // Save lead asynchronously (don't block OTP sending)
        saveClientData(leadData).catch(error => {
          console.error('Error saving lead:', error);
          // Don't show error to user - lead saving is secondary
        });
      } catch (leadError) {
        console.error('Error saving lead:', leadError);
        // Continue with OTP sending even if lead save fails
      }

      const phoneNumber = `+91${phone}`;
      console.log('📱 Sending OTP to:', phoneNumber);
      
      const confirmation = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        recaptchaVerifierRef.current
      );
      
      console.log('✅ OTP sent successfully');
      setConfirmationResult(confirmation);
      setOtpSent(true);
      setError('');
      setResendTimer(60); // Start 60 second timer
      
      // Play notification sound and show browser notification
      notifyOTPSent(phoneNumber);
    } catch (error) {
      console.error('❌ Error sending OTP:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      let errorMessage = 'Failed to send OTP. Please try again.';
      
      if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      } else if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number. Please check and try again.';
      } else if (error.code === 'auth/quota-exceeded') {
        errorMessage = 'SMS quota exceeded. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setOtpSent(false);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    if (!confirmationResult) {
      setError('OTP session expired. Please request a new OTP.');
      return;
    }

    setVerifyingOtp(true);
    setError('');

    try {
      const result = await confirmationResult.confirm(otp);
      
      // OTP verified successfully
      setOtpVerified(true);
      setError('');
      
      // Submit the form
      await handleSubmit();
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setError('Invalid OTP. Please check and try again.');
      setOtpVerified(false);
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) {
      return; // Don't allow resend if timer is still running
    }

    setError('');
    setSendingOtp(true);

    try {
      // Re-initialize reCAPTCHA if needed
      if (!recaptchaVerifierRef.current) {
        if (!recaptchaContainerRef.current) {
          setError('reCAPTCHA not initialized. Please refresh the page.');
          setSendingOtp(false);
          return;
        }

        recaptchaVerifierRef.current = new RecaptchaVerifier(
          auth,
          recaptchaContainerRef.current,
          {
            size: 'invisible',
            callback: () => {
              console.log('✅ reCAPTCHA solved');
            },
            'expired-callback': () => {
              console.error('❌ reCAPTCHA expired');
              recaptchaVerifierRef.current = null;
            }
          }
        );
      }

      const phoneNumber = `+91${phone}`;
      const confirmation = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        recaptchaVerifierRef.current
      );
      
      setConfirmationResult(confirmation);
      setOtpSent(true);
      setOtp('');
      setError('');
      setResendTimer(60); // Restart 60 second timer
      
      // Play notification sound and show browser notification
      notifyOTPSent(phoneNumber);
    } catch (error) {
      console.error('Error resending OTP:', error);
      let errorMessage = 'Failed to resend OTP. Please try again.';
      
      if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const dataToSave = {
        email: email.toLowerCase().trim(),
        phone: `+91${phone}`,
        source: 'popup',
        status: 'new',
      };

      const result = await saveClientData(dataToSave);

      if (result.success) {
        setSuccess(true);
        
        // Close popup after 2 seconds
        setTimeout(() => {
          setShowPopup(false);
          // Reset form
          setEmail('');
          setPhone('');
          setOtp('');
          setOtpSent(false);
          setOtpVerified(false);
          setSuccess(false);
          setResendTimer(0);
          setConfirmationResult(null);
        }, 2000);
      } else {
        setError(result.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting popup data:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full relative animate-fade-in">
        {/* Close/Skip Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Skip"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-8">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-light mb-2 text-gray-900">Thank You!</h3>
              <p className="text-gray-600 font-light">We'll get back to you soon.</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="text-center mb-6">
                <h2 className="text-2xl md:text-3xl font-light mb-2 text-gray-900 tracking-tight">
                  Get Your Free Quote
                </h2>
                <p className="text-gray-600 font-light text-sm">
                  Share your details and we'll provide an instant estimate
                </p>
              </div>

              {/* Form */}
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-gray-700 font-light mb-2 text-sm">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all font-light text-sm"
                    disabled={otpSent || isSubmitting}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-gray-700 font-light mb-2 text-sm">
                    Phone Number *
                  </label>
                  <div className="flex gap-2">
                    <div className="flex items-center px-3 border border-gray-300 rounded-lg bg-gray-50">
                      <span className="text-gray-600 font-light text-sm">+91</span>
                    </div>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="9741155118"
                      maxLength="10"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all font-light text-sm"
                      disabled={otpSent || isSubmitting}
                    />
                  </div>
                </div>

                {/* OTP Section */}
                {otpSent && !otpVerified && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-700 font-light mb-2 text-sm">
                        Enter OTP *
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          maxLength="6"
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all font-light text-sm text-center text-2xl tracking-widest"
                          disabled={verifyingOtp || isSubmitting}
                        />
                        <button
                          onClick={handleVerifyOtp}
                          disabled={verifyingOtp || otp.length !== 6}
                          className="px-6 py-3 bg-black hover:bg-gray-900 text-white rounded-lg font-light text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {verifyingOtp ? 'Verifying...' : 'Verify'}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <button
                        onClick={handleResendOtp}
                        disabled={sendingOtp || resendTimer > 0}
                        className={`text-sm font-light transition-colors ${
                          resendTimer > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                      </button>
                      <button
                        onClick={() => {
                          setOtpSent(false);
                          setOtp('');
                          setError('');
                          setConfirmationResult(null);
                          setResendTimer(0);
                        }}
                        className="text-sm text-gray-600 hover:text-gray-900 font-light"
                      >
                        Change phone number
                      </button>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-light">
                    {error}
                  </div>
                )}

                {/* Buttons */}
                <div className="pt-4">
                  {!otpSent ? (
                    <button
                      onClick={handleSendOtp}
                      disabled={sendingOtp || isSubmitting || !email || !phone}
                      className="w-full bg-black hover:bg-gray-900 text-white px-6 py-3 rounded-lg font-light text-sm tracking-wide transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {sendingOtp ? 'Sending OTP...' : 'Send OTP'}
                    </button>
                  ) : null}

                  <button
                    onClick={handleSkip}
                    className="w-full mt-3 text-gray-600 hover:text-gray-900 font-light text-sm transition-colors"
                    disabled={isSubmitting || sendingOtp}
                  >
                    Skip for now
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactPopup;
